import { Spin } from 'antd';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoCircleFilled } from '@ant-design/icons';
import { PlumbingProduct } from '@common/models';
import { SectionSubtitle } from '@core/components';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { usePlumbing } from '@ee-api/plumbing-api/usePlumbing';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
    addSelectedPlumbing,
    removeSelectedPlumbing,
    selectSelectedPlumbing,
    updateSelectedPlumbing,
} from '@redux/slices/plumbingSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import { PlumbingItem } from './PlumbingItem';
import { PlumbingModal } from './PlumbingModal';

import './PlumbingSection.scss';

export const PlumbingSection = () => {
    const { t } = useTranslation();
    const { plumbingItems, isFetchingPlumbing } = usePlumbing();

    const zipcode = useAppSelector(selectZipcode);
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const selectedPlumbing = useAppSelector(selectSelectedPlumbing);

    const { areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);

    const dispatch = useAppDispatch();
    const [isModalVisible, setIsModalVisible] = useState(false);

    const handlePlumbingItemClick = (item: PlumbingProduct) => {
        const currentSelectedPlumbing = selectedPlumbing.get(item.productId);
        if (currentSelectedPlumbing) {
            if (currentSelectedPlumbing.isManuallyChanged) {
                // TODO(Alyson) This basically handle the fucntionality of deselecting a
                // plumbing item after changing its value. The value must be remembered.
                // We can improve the way this is done only adding to selectedPlumbing
                // the plumbingItems that are REALLY selected.
                const selectedPlumbingClone = new Map(selectedPlumbing);
                selectedPlumbingClone.set(item.productId, {
                    quantity: currentSelectedPlumbing.quantity === 0 ? 1 : currentSelectedPlumbing.quantity,
                    isSelected: !currentSelectedPlumbing.isSelected,
                    isManuallyChanged: currentSelectedPlumbing.isManuallyChanged,
                });
                dispatch(updateSelectedPlumbing(selectedPlumbingClone));
            } else {
                dispatch(removeSelectedPlumbing(item));
            }
        } else {
            dispatch(addSelectedPlumbing(item));
        }
    };

    const updateQuantity = (product: PlumbingProduct, quantity: number) => {
        if (quantity < 0) {
            return;
        }

        const newSelectedPlumbingMap = new Map(selectedPlumbing);
        const selectedItem = newSelectedPlumbingMap.get(product.productId);

        if (!selectedItem) {
            return;
        }

        newSelectedPlumbingMap.set(product.productId, {
            quantity,
            isSelected: selectedItem.isSelected && quantity > 0,
            isManuallyChanged: true,
        });
        dispatch(updateSelectedPlumbing(newSelectedPlumbingMap));
    };

    const changeQuantity = (product: PlumbingProduct, quantity: number): void => {
        const newSelectedPlumbingMap = new Map(selectedPlumbing);
        const selectedItem = newSelectedPlumbingMap.get(product.productId);

        if (!selectedItem) {
            return;
        }

        const updatedQuantity = selectedItem.quantity + quantity;
        if (updatedQuantity < 0) {
            return;
        }

        newSelectedPlumbingMap.set(product.productId, {
            quantity: updatedQuantity,
            isSelected: selectedItem.isSelected && updatedQuantity > 0,
            isManuallyChanged: true,
        });
        dispatch(updateSelectedPlumbing(newSelectedPlumbingMap));
    };

    const arePlumbingAvailable =
        zipcode && selectedStore && selectedMaterial && areThereShapesInTheDrawingArea && plumbingItems.length;

    if (!arePlumbingAvailable) {
        return <></>;
    }

    return (
        <>
            <SectionSubtitle
                name={t('SCROLLABLE_AREA.PLUMBING.TITLE')}
                description={t('SCROLLABLE_AREA.PLUMBING.DESCRIPTION')}
                showLine={false}
                additionalComponent={
                    <InfoCircleFilled
                        className="PlumbingSection PlumbingSection__info-icon"
                        onClick={() => setIsModalVisible(true)}
                    />
                }
            />
            {isFetchingPlumbing ? (
                <Spin style={{ fontSize: 24 }} />
            ) : (
                <div>
                    <div className="card-with-quantity card-with-quantity--large">
                        {plumbingItems.map((item, id) => (
                            <PlumbingItem
                                key={id}
                                product={item}
                                isSelected={selectedPlumbing.get(item.productId)?.isSelected}
                                quantity={selectedPlumbing.get(item.productId)?.quantity}
                                updateQuantity={updateQuantity}
                                changeQuantity={changeQuantity}
                                onClick={() => handlePlumbingItemClick(item)}
                            />
                        ))}
                    </div>
                    <PlumbingModal isVisible={isModalVisible} onCancel={() => setIsModalVisible(false)} />
                </div>
            )}
        </>
    );
};
